'use client';

import { useState, useMemo } from 'react';
import { FindingEditDrawer } from '../../../../findings/FindingEditDrawer';
import { EnhancedEditFindingsModal } from '../../EnhancedEditFindingsModal';
import { useFindingSearchContext } from '../../../finding-search';
import {
  findingToolHitlConfig,
  findingsActionHitlConfig,
} from '../../tools/finding/hitl-config';
import {
  convertToEnrichedFinding,
  convertToFindingToolInput,
} from '../utils/finding-converters';
import { Spinner } from '../../../../../components/spinner';
import type { FindingData } from '../types';
import type { InternalEnrichedFinding } from '../../../../../types';
import type { EditableFields } from '../../../../findings/finding-editable-fields';
import type { EditModalProps } from '../../tool-config-types';

type FindingEditModalWrapperProps = EditModalProps;

export function FindingEditModalWrapper({
  isOpen,
  onClose,
  onSubmit,
  toolName,
  args,
  extractedData = [],
  defaultCvssVersion: defaultCvssVersionProp,
}: FindingEditModalWrapperProps) {
  const {
    onSearchSignals,
    onSearchAssets,
    defaultCvssVersion: contextCvssVersion,
    cvssEnvironmentalRequirements,
  } = useFindingSearchContext();
  const defaultCvssVersion = defaultCvssVersionProp ?? contextCvssVersion;

  const [findingDrawerMode, setFindingDrawerMode] = useState<'view' | 'edit'>(
    'edit',
  );

  const hitlConfig =
    toolName === 'finding-tool'
      ? findingToolHitlConfig
      : findingsActionHitlConfig;

  const singleFinding =
    extractedData.length === 1 ? (extractedData[0] as FindingData) : undefined;

  const editingFinding = useMemo<InternalEnrichedFinding | null>(() => {
    if (!singleFinding) {
      return null;
    }

    return convertToEnrichedFinding(singleFinding);
  }, [singleFinding]);

  const handleDrawerSave = async (
    _findingId: string,
    updates: Partial<EditableFields>,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!editingFinding) {
      return { success: false, error: 'No finding to edit' };
    }

    const findingToolInput = convertToFindingToolInput({
      original: editingFinding,
      updates,
      originalArgs: args,
    });

    onSubmit(findingToolInput);
    onClose();

    return { success: true };
  };

  const handleModalSubmit = (editedFindings: FindingData[]) => {
    if (!hitlConfig) return;
    const wrappedData = hitlConfig.wrapEditedData(editedFindings);

    onSubmit(wrappedData);
    onClose();
  };

  if (!isOpen) return null;

  if (extractedData.length === 1) {
    if (!editingFinding) {
      return (
        <div className="flex justify-center p-6">
          <Spinner size="sm" />
        </div>
      );
    }

    return (
      <FindingEditDrawer
        finding={editingFinding}
        open={isOpen}
        onClose={onClose}
        onSave={handleDrawerSave}
        mode={findingDrawerMode}
        onModeChange={setFindingDrawerMode}
        onSearchSignals={onSearchSignals}
        onSearchAssets={onSearchAssets}
        {...(defaultCvssVersion ? { defaultCvssVersion } : {})}
        cvssSection="hidden"
        environmentalRequirements={cvssEnvironmentalRequirements}
        owaspRiskEditMode="deferred"
      />
    );
  }

  return (
    <EnhancedEditFindingsModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleModalSubmit}
      findings={extractedData as FindingData[]}
      actionName={toolName}
    />
  );
}
